/**
 * Sycamores MCP Outreach Billing (demo).
 *
 * Staff log SMS/phone outreach touches (with durations) and pre-enrollment
 * dispositions for MCP-referred consumers; the page then generates a separate
 * billing file per payer using two different rules, and shows the reconciliation.
 *
 *   - L.A. Care  (per-minute): bill every touch regardless of disposition,
 *                 $30 per 15-min increment, rounded up, min 15 min.
 *   - Health Net (flat):       $125 per consumer that reached a disposition.
 */
import { useTabbedChrome } from '@/components/weavein';
import React, { useEffect, useState } from 'react';
import {
  Phone,
  MessageSquare,
  Download,
  RotateCcw,
  Play,
  Plus,
  FileText,
  CheckCircle2,
  XCircle,
  Loader2,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import outreachBillingApi, {
  OutreachConsumer,
  OutreachPreview,
  BillableActivity,
  OutreachRunResult,
} from '@/services/api/outreachBilling';

const PERIOD = '2026-06';
const DISPOSITIONS = [
  { value: '', label: '— none (in outreach) —' },
  { value: 'enrolled', label: 'Enrolled' },
  { value: 'declined', label: 'Declined' },
  { value: 'excluded', label: 'Excluded' },
];

interface Props {
  onNavigate?: (tab: string) => void;
}

const money = (n: number) => `$${(n ?? 0).toFixed(2)}`;

export default function OutreachBillingPage({ onNavigate }: Props) {
  const tabbed = useTabbedChrome();
  const [consumers, setConsumers] = useState<OutreachConsumer[]>([]);
  const [preview, setPreview] = useState<OutreachPreview | null>(null);
  const [billable, setBillable] = useState<BillableActivity[]>([]);
  const [runs, setRuns] = useState<OutreachRunResult | null>(null);
  const [runStatus, setRunStatus] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    try {
      setConsumers(await outreachBillingApi.getConsumers());
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load consumers');
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    setStatus(null);
    try {
      await fn();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Something went wrong');
    } finally {
      setBusy(null);
    }
  };

  const onLoadSample = () =>
    run('sample', async () => {
      const r = await outreachBillingApi.loadSample();
      setPreview(null);
      setBillable([]);
      setRuns(null);
      await refresh();
      setStatus(`Loaded sample scenario (${r.consumers_loaded} consumers).`);
    });

  const onReset = () =>
    run('reset', async () => {
      const r = await outreachBillingApi.resetDemo();
      setPreview(null);
      setBillable([]);
      setRuns(null);
      await refresh();
      setStatus(`Reset complete — removed ${r.claims_removed} generated claim(s).`);
    });

  const onPreview = () =>
    run('preview', async () => {
      const [pv, ba] = await Promise.all([
        outreachBillingApi.preview(PERIOD),
        outreachBillingApi.getBillableActivities(PERIOD),
      ]);
      setPreview(pv);
      setBillable(ba.activities);
      setStatus('Preview reconciled.');
    });

  const onGenerate = () =>
    run('generate', async () => {
      const result = await outreachBillingApi.generateRuns(PERIOD);
      setRuns(result);
      setRunStatus(Object.fromEntries(result.runs.map((r) => [r.run_id, 'generated'])));
      setStatus(`Generated ${result.runs.length} billing file(s), total ${money(result.grand_total)}.`);
    });

  const onApprove = (runId: number) =>
    run(`approve-${runId}`, async () => {
      const r = await outreachBillingApi.approveRun(runId);
      setRunStatus((s) => ({ ...s, [runId]: r.status }));
      setStatus('Run approved — ready to submit for reimbursement.');
    });

  const onSubmit = (runId: number) =>
    run(`submit-${runId}`, async () => {
      const r = await outreachBillingApi.submitRun(runId);
      setRunStatus((s) => ({ ...s, [runId]: r.status }));
      setStatus(`Submitted ${r.claims_submitted} claim(s); ${r.claims_paid} paid (mock remittance).`);
    });

  const download = (runId: number, format: 'csv' | 'html' | '837') =>
    run(`file-${runId}-${format}`, async () => {
      const { blob, filename } = await outreachBillingApi.fetchFile(runId, format);
      const url = URL.createObjectURL(blob);
      if (format === 'html') {
        window.open(url, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div>
        {!tabbed && (
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
          MCP Billing Review
        </h1>
        )}
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Pre-enrollment outreach → two-payer, two-rule billing files · service period June 2026
        </p>
      </div>

      {/* Rules explainer */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-lg border border-teal-200 bg-teal-50 dark:bg-teal-950/30 dark:border-teal-900 p-3">
          <div className="font-medium text-teal-800 dark:text-teal-300">L.A. Care — per minute</div>
          <div className="text-sm text-teal-700 dark:text-teal-400">
            Bills <b>every</b> outreach touch regardless of disposition. $30 per 15-min increment,
            rounded up, minimum 15 min.
          </div>
        </div>
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 dark:bg-indigo-950/30 dark:border-indigo-900 p-3">
          <div className="font-medium text-indigo-800 dark:text-indigo-300">Health Net — flat per disposition</div>
          <div className="text-sm text-indigo-700 dark:text-indigo-400">
            $125 flat per consumer that reached a <b>disposition</b> (enroll / decline / exclude).
            No disposition ⇒ $0.
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={onLoadSample} disabled={!!busy}>
          {busy === 'sample' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
          Load sample scenario
        </Button>
        <Button variant="outline" onClick={onReset} disabled={!!busy}>
          {busy === 'reset' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
          Reset demo
        </Button>
        <div className="flex-1" />
        <Button variant="outline" onClick={onPreview} disabled={!!busy}>
          {busy === 'preview' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
          Preview billing
        </Button>
        <Button onClick={onGenerate} disabled={!!busy}>
          {busy === 'generate' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
          Generate billing files
        </Button>
      </div>

      {(status || error) && (
        <div
          className={`text-sm rounded-md px-3 py-2 ${
            error
              ? 'bg-red-50 text-red-700 border border-red-200'
              : 'bg-green-50 text-green-700 border border-green-200'
          }`}
        >
          {error || status}
        </div>
      )}

      {/* Consumer roster */}
      <Card>
        <CardHeader>
          <CardTitle>Consumers ({consumers.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {consumers.length === 0 && (
            <div className="text-sm text-gray-500">
              No consumers found. Ensure migration 514 has been applied for this org.
            </div>
          )}
          {consumers.map((c) => (
            <ConsumerRow key={c.enrollment_id} consumer={c} busy={busy} onChange={refresh} setError={setError} />
          ))}
        </CardContent>
      </Card>

      {/* Billable / reimbursable activities (before generating a billing file) */}
      {billable.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Billable activities ({billable.filter((a) => a.included).length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-1">Consumer</th>
                    <th>Payer</th>
                    <th>Channel</th>
                    <th>Date</th>
                    <th className="text-right">Min</th>
                    <th>POS</th>
                    <th className="text-right">Amount</th>
                    <th>Billable</th>
                  </tr>
                </thead>
                <tbody>
                  {billable.map((a, i) => (
                    <tr key={i} className={a.included ? '' : 'text-gray-400'}>
                      <td className="py-1">{a.consumer_name}</td>
                      <td>{a.payer_name}</td>
                      <td>{a.channel}</td>
                      <td>{a.date || '—'}</td>
                      <td className="text-right">{a.minutes ?? '—'}</td>
                      <td>{a.place_of_service}</td>
                      <td className="text-right">{money(a.amount)}</td>
                      <td>{a.included ? 'yes' : (a.exclusion_reason || 'no')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview reconciliation */}
      {preview && (
        <Card>
          <CardHeader>
            <CardTitle>
              Preview — {preview.period_start} to {preview.period_end} · grand total {money(preview.grand_total)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {preview.payers.map((p) => (
              <div key={p.payer_id} className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium">{p.payer_name}</div>
                  <div className="text-sm text-gray-500">{p.rule_type}</div>
                  <div className="font-semibold">{money(p.total_amount)}</div>
                </div>
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="py-1">Consumer</th>
                      <th>Disposition</th>
                      <th className="text-right">Touches</th>
                      <th className="text-right">Units</th>
                      <th className="text-right">Amount</th>
                      <th>Billed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.lines.map((ln) => (
                      <tr key={ln.enrollment_id} className={ln.included ? '' : 'text-gray-400'}>
                        <td className="py-1">{ln.consumer_name}</td>
                        <td>{ln.disposition || '—'}</td>
                        <td className="text-right">{ln.activity_count}</td>
                        <td className="text-right">{ln.billable_units}</td>
                        <td className="text-right">{money(ln.amount)}</td>
                        <td>
                          {ln.included ? (
                            <span className="inline-flex items-center gap-1 text-green-600">
                              <CheckCircle2 className="w-4 h-4" /> yes
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-gray-400" title={ln.exclusion_reason || ''}>
                              <XCircle className="w-4 h-4" /> {ln.exclusion_reason || 'excluded'}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Generated billing files */}
      {runs && (
        <Card>
          <CardHeader>
            <CardTitle>Billing files · grand total {money(runs.grand_total)}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {runs.runs.map((r) => (
              <div key={r.run_id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="font-medium">{r.payer_name}</div>
                  <div className="text-lg font-semibold">{money(r.total_amount)}</div>
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {r.rule_type} · {r.consumer_count} consumer(s) · {r.activity_count} touch(es) · {r.claim_count} claim(s)
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-gray-400">{r.file_name}</span>
                  <StatusBadge status={runStatus[r.run_id] || 'generated'} />
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => download(r.run_id, 'csv')}>
                    <Download className="w-4 h-4 mr-1" /> CSV
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => download(r.run_id, 'html')}>
                    <FileText className="w-4 h-4 mr-1" /> Invoice
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => download(r.run_id, '837')}>
                    <Download className="w-4 h-4 mr-1" /> EDI 837
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {(runStatus[r.run_id] || 'generated') === 'generated' && (
                    <Button size="sm" onClick={() => onApprove(r.run_id)} disabled={!!busy}>
                      {busy === `approve-${r.run_id}` ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Approve
                    </Button>
                  )}
                  {(runStatus[r.run_id] || '') === 'approved' && (
                    <Button size="sm" onClick={() => onSubmit(r.run_id)} disabled={!!busy}>
                      {busy === `submit-${r.run_id}` ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />} Submit for reimbursement
                    </Button>
                  )}
                </div>
              </div>
            ))}
            <div className="md:col-span-2">
              <Button variant="ghost" size="sm" onClick={() => onNavigate?.('billing-claims')}>
                View generated claims on the Claims page →
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    generated: 'bg-gray-100 text-gray-700',
    approved: 'bg-blue-100 text-blue-800',
    submitted: 'bg-amber-100 text-amber-800',
    paid: 'bg-green-100 text-green-800',
  };
  return <Badge className={map[status] || 'bg-gray-100 text-gray-700'}>{status}</Badge>;
}

// ---------------------------------------------------------------------------
// Consumer row with inline touch logging + disposition
// ---------------------------------------------------------------------------
function ConsumerRow({
  consumer,
  busy,
  onChange,
  setError,
}: {
  consumer: OutreachConsumer;
  busy: string | null;
  onChange: () => Promise<void>;
  setError: (s: string | null) => void;
}) {
  const [channel, setChannel] = useState<'sms' | 'phone'>('phone');
  const [minutes, setMinutes] = useState('15');
  const [tdate, setTdate] = useState('2026-06-15');
  const [saving, setSaving] = useState(false);

  const addTouch = async () => {
    setSaving(true);
    setError(null);
    try {
      await outreachBillingApi.logTouch(consumer.member_id, {
        channel,
        minutes: parseFloat(minutes) || 0,
        date: tdate,
      });
      await onChange();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to log touch');
    } finally {
      setSaving(false);
    }
  };

  const changeDisposition = async (value: string) => {
    setSaving(true);
    setError(null);
    try {
      await outreachBillingApi.setDisposition(consumer.member_id, { disposition: value || null });
      await onChange();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to set disposition');
    } finally {
      setSaving(false);
    }
  };

  const isLaCare = /l\.?a\.? *care/i.test(consumer.payer_name || '');

  return (
    <div className="border rounded-lg p-3">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div>
          <div className="font-medium">
            {consumer.name}{' '}
            <span className="text-xs text-gray-400">({consumer.member_number})</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={isLaCare ? 'bg-teal-100 text-teal-800' : 'bg-indigo-100 text-indigo-800'}>
              {consumer.payer_name || 'No payer'}
            </Badge>
            {consumer.pre_authorized && (
              <span className="text-xs text-gray-500">Prior auth {consumer.prior_auth_number}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Disposition</label>
          <select
            className="border rounded px-2 py-1 text-sm bg-white dark:bg-gray-800"
            value={consumer.disposition || ''}
            disabled={saving || !!busy}
            onChange={(e) => changeDisposition(e.target.value)}
          >
            {DISPOSITIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Touches */}
      <div className="mt-2 flex flex-wrap gap-2">
        {consumer.touches.length === 0 && (
          <span className="text-xs text-gray-400">No outreach touches logged yet.</span>
        )}
        {consumer.touches.map((t) => (
          <span
            key={t.id}
            className="inline-flex items-center gap-1 text-xs bg-gray-100 dark:bg-gray-800 rounded px-2 py-1"
          >
            {t.channel === 'phone' ? <Phone className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
            {t.channel} · {t.minutes}m {t.date ? `· ${t.date}` : ''}
          </span>
        ))}
      </div>

      {/* Add touch */}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select
          className="border rounded px-2 py-1 text-sm bg-white dark:bg-gray-800"
          value={channel}
          onChange={(e) => setChannel(e.target.value as 'sms' | 'phone')}
        >
          <option value="phone">phone</option>
          <option value="sms">sms</option>
        </select>
        <input
          type="number"
          min="1"
          className="border rounded px-2 py-1 text-sm w-16 sm:w-20 bg-white dark:bg-gray-800"
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
          placeholder="min"
        />
        <input
          type="date"
          className="border rounded px-2 py-1 text-sm bg-white dark:bg-gray-800"
          value={tdate}
          onChange={(e) => setTdate(e.target.value)}
        />
        <Button size="sm" variant="outline" onClick={addTouch} disabled={saving || !!busy}>
          <Plus className="w-4 h-4 mr-1" /> Log touch
        </Button>
      </div>
    </div>
  );
}
